import React, { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { MessageCircle, TrendingUp, Target, Sparkles, Check } from 'lucide-react';
import { state } from '../../store/global';
import {
  getConsentStatus,
  saveConsent,
  getLocalConsentFlag,
} from '../../lib/consent';

/**
 * ConsentModal — captura del consentimiento de WhatsApp para clientes.
 *
 * Se monta UNA sola vez en el Layout del dashboard. Se auto-decide:
 *  - Si el usuario YA decidio (backend o flag local) -> no se muestra.
 *  - Si NO ha decidido -> aparece una vez, con opt-in EXPRESO y OPCIONAL.
 *
 * El momento natural: el cliente acaba de verificar su telefono por SMS al
 * entrar, asi que su numero de WhatsApp esta confirmado. No molesta: cualquiera
 * de los dos botones registra la decision y ya no vuelve a aparecer.
 *
 * Requisitos legales (Ley 1581): consentimiento EXPRESO (clic afirmativo, nada
 * pre-marcado), SEPARADO de los Terminos, OPCIONAL, y auditable (el backend
 * guarda canal, fecha, version de politica, numero e IP).
 */
export default function ConsentModal() {
  const snap = useSnapshot(state);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Necesitamos un usuario autenticado con telefono para pedir consentimiento.
    const phone = snap.user?.phonenumber?.number;
    if (!snap.isAuthenticated || !phone) return;

    // 1) Flag local: si ya decidio en este navegador, no molestar.
    if (getLocalConsentFlag()) return;

    // 2) Fuente de verdad: preguntar al backend. Si no responde (aun no
    //    desplegado), caemos a mostrarlo una vez (el flag local evitara repetir).
    (async () => {
      const status = await getConsentStatus();
      if (cancelled) return;
      if (status && status.decided) return; // ya consintio/rechazo en el servidor
      // Pequeno respiro para que no salte de golpe al entrar.
      setTimeout(() => { if (!cancelled) setShow(true); }, 1200);
    })();

    return () => { cancelled = true; };
  }, [snap.isAuthenticated, snap.user?.phonenumber?.number]);

  const telefono = (() => {
    const nat = snap.user?.phonenumber?.nationality?.area_code || '';
    const num = snap.user?.phonenumber?.number || '';
    return num ? `${nat}${num}` : null;
  })();

  const handleDecision = async (otorgado) => {
    if (saving) return;
    setSaving(true);
    await saveConsent(otorgado, { telefono, fuente: 'modal_inapp' });
    setSaving(false);
    if (otorgado) {
      setDone(true);
      setTimeout(() => setShow(false), 1800); // muestra la confirmacion y cierra
    } else {
      setShow(false);
    }
  };

  if (!show) return null;

  const masked = telefono
    ? `${telefono.slice(0, 3)}···${telefono.slice(-4)}`
    : 'tu telefono';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 font-[poppins]">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 w-full max-w-[420px] bg-white rounded-2xl shadow-2xl overflow-hidden">
        {done ? (
          // Confirmacion: el primer toque debe sentirse valioso, no una venta.
          <div className="px-8 py-12 text-center">
            <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-[#25D366]/15 flex items-center justify-center">
              <Check size={30} className="text-[#25D366]" />
            </div>
            <h2 className="text-lg font-bold text-[#092F7B]">¡Listo!</h2>
            <p className="mt-2 text-sm text-[#717680]">
              Alex te va a acompañar por WhatsApp. Pronto tendrás noticias.
            </p>
          </div>
        ) : (
          <>
            {/* Encabezado */}
            <div className="bg-gradient-to-br from-[#092F7B] to-[#2A59C6] px-7 pt-7 pb-6 text-white">
              <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center mb-3">
                <MessageCircle size={26} className="text-white" />
              </div>
              <h2 className="text-xl font-bold leading-tight">
                ¿Quieres que Alex te acompañe por WhatsApp?
              </h2>
              <p className="mt-1 text-sm text-white/80 font-normal">
                Te escribimos solo al número que ya verificaste: <span className="font-semibold">{masked}</span>
              </p>
            </div>

            {/* Beneficios: valor, no permiso */}
            <div className="px-7 py-5 space-y-3">
              <Benefit icon={<TrendingUp size={18} />} text="Tu extracto del mes, apenas esté listo" />
              <Benefit icon={<Target size={18} />} text="El avance de tus metas" />
              <Benefit icon={<Sparkles size={18} />} text="Oportunidades en lo que te interesa" />
              <Benefit icon={<MessageCircle size={18} />} text="Y Alex te responde cuando le escribas" />
            </div>

            {/* Botones — opt-in EXPRESO (nada pre-marcado) */}
            <div className="px-7 pb-4 space-y-2">
              <button
                onClick={() => handleDecision(true)}
                disabled={saving}
                className="w-full py-3 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold text-sm transition-colors disabled:opacity-60"
              >
                {saving ? 'Activando…' : 'Sí, activar WhatsApp'}
              </button>
              <button
                onClick={() => handleDecision(false)}
                disabled={saving}
                className="w-full py-3 rounded-xl bg-white text-[#717680] font-medium text-sm hover:bg-[#F5F5F5] transition-colors disabled:opacity-60"
              >
                Ahora no
              </button>
            </div>

            {/* Letra legal — separada de los Terminos, opcional y revocable */}
            <div className="px-7 pb-6">
              <p className="text-[11px] leading-[16px] text-[#98A2B3] text-center">
                Es opcional y puedes cancelar cuando quieras. Al activar, autorizas a
                FlowexPay a enviarte estos mensajes por WhatsApp según la{' '}
                <a
                  href="/policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2A59C6] underline"
                >
                  Política de Privacidad
                </a>.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Benefit({ icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-[#E6EBF7] flex items-center justify-center text-[#092F7B] shrink-0">
        {icon}
      </div>
      <span className="text-sm text-[#333333]">{text}</span>
    </div>
  );
}
